/**
 * @fileoverview One group of the What to plant list: the server's heading, its rows (name, the planting line, the
 * server's tag) and, when the server cut the group, "Show N more" (D67).
 *
 * The app decides nothing here: the heading, the order, the rows, the pill and how many rows are drawn first
 * (`shown`) are the server's. Showing the rest is UI state only (no second call, no "Show less").
 *
 * A row is one of the gardener's plants; a tap opens that plant's page, /plants/<plantId>, where its Plant now button is.
 *
 * Where: WhatToPlantPage.
 * Words (en/es): whatToPlant.showMore.
 *
 * @module WhatToPlantGroup
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Pill } from '@/shared/components/ui/Pill';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { cn } from '@/lib/utils';
import type { WhatToPlantGroup as Group } from '@/shared/api/whatToPlant';

/** What a group is handed. */
interface WhatToPlantGroupProps {
  /** The group, as the server sent it. */
  group: Group;
}

/**
 * A heading over a card of tappable rows, with "Show N more" under the rows the server cut.
 *
 * @returns The group.
 */
export const WhatToPlantGroup: React.FC<WhatToPlantGroupProps> = ({ group }) => {
  const { reactTranslate: t } = useTranslation();
  /** The rest of the rows have been asked for. */
  const [all, setAll] = React.useState(false);
  const rows = all ? group.rows : group.rows.slice(0, group.shown);
  const hidden = group.rows.length - rows.length;
  return (
    <section className="mt-5" aria-label={group.heading}>
      <h2 className={cn(
        'mb-1.5 text-xs font-semibold uppercase tracking-wide',
        group.urgent ? 'text-accent-ink' : 'text-muted-foreground',
      )}
      >
        {group.heading}
      </h2>
      <ul className="divide-y divide-border overflow-hidden rounded-lg border bg-card">
        {rows.map((row) => (
          <li key={row.plantId}>
            <Link
              to={`/plants/${row.plantId}`}
              className="flex min-h-14 w-full items-center gap-3 px-3 py-2 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            >
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-base font-semibold text-foreground">{row.name}</span>
                  {row.tag && <Pill size="sm" tone="outline">{row.tag}</Pill>}
                </span>
                <span className="block text-sm text-muted-foreground">{row.line}</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setAll(true)}
          className="mt-2 min-h-11 text-sm font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {t('whatToPlant.showMore', { count: hidden })}
        </button>
      )}
    </section>
  );
};
